import { TimeInForceType } from '@/compiled_proto/com/celertech/orderrouting/api/enums/TimeInForceTypeProto';
import { createLumeFxRestClient } from '@/services/LumeFxService';
import { addMarketFull } from '@/services/MarketService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials, selectCurrentLuceraAccountName } from '@/state/reducers/authSlice';
import { OrderPrefillValues } from '@/state/reducers/orderSlice';
import { LumeFxOrderType, LumeFxSide, LumeFxTimeInForce } from '@/types/lumeFx';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import useLimitOrder from '@/utils/hooks/useLimitOrder';
import { UseOrderExecutionReturn } from '@/utils/hooks/useOrderExecution';
import useTotals from '@/utils/hooks/useTotals';
import useTradeFeePreview from '@/utils/hooks/useTradeFeePreview';
import { useLucera } from '@/utils/providers/LuceraProvider';
import { useScreenSizes } from '@/utils/providers/ScreenSizesProvider';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDebouncedValue } from '@mantine/hooks';
import BigNumber from 'bignumber.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { isTablet } from 'react-device-detect';
import { FormProvider, useForm } from 'react-hook-form';
import FeePreview from '../common/FeePreview';
import Loader from '../common/Loader';
import Tooltip from '../common/Tooltip';
import ExecutionInputController from '../inputs/ExecutionInputController';
import RHFNumberInput from '../inputs/RHFNumberInput';
import Select from '../inputs/Select';
import SlippageInput from '../inputs/SlippageInput';
import { BalancePreview } from './components/BalancePreview';
import FormBody from './components/FormBody';
import FormFooter from './components/FormFooter';
import FormOrderTypeSwitch from './components/FormOrderTypeSwitch';
import FormSubmit from './components/FormSubmit';
import Totals from './components/Totals';
import { LimitOrderFormInput, LimitOrderFormValues, limitOrderSchema } from './schema/limitOrderSchema';

const durationOptions: { label: keyof typeof TimeInForceType; value: TimeInForceType }[] = [
    { label: 'DAY', value: TimeInForceType.DAY },
    { label: 'IOC', value: TimeInForceType.IOC },
    { label: 'FOK', value: TimeInForceType.FOK },
    { label: 'GTC', value: TimeInForceType.GTC }
];

export const defaultValues: Partial<LimitOrderFormInput> = {
    limitPrice: null,
    quantity: null,
    duration: durationOptions[0],
    slippage: '0.00000'
};

interface LimitOrderFormProps extends UseOrderExecutionReturn {
    parentSize?: number;
    prefillValues?: OrderPrefillValues;
    onClose?: () => void;
    closeOnSubmit?: boolean;
}

const luceraRestURL = window.config?.lucera?.rest;

function LimitOrderForm(props: LimitOrderFormProps) {
    const { parentSize = 0, prefillValues, onClose, closeOnSubmit = false, ...orderExecutionProps } = props;
    const {
        mode,
        ccy1,
        ccy2,
        sideState,
        currencyState,
        priceCurrency,
        activeCurrencyConfig,
        orderBook,
        instrument,
        pairMapToUse,
        minOrderSize,
        orderDecimals,
        ccy1Config,
        isCcy2Order
    } = orderExecutionProps;

    const lang = useLanguage();
    const screen = useScreenSizes();
    const credentials = useAppSelector(selectCredentials);
    const currentLuceraAccountName = useAppSelector(selectCurrentLuceraAccountName);

    const { jwt } = useLucera();

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const [side, setSide] = sideState;
    const [currency, setCurrency] = currencyState;
    const [stateLoading, setStateLoading] = useState(false);

    const { asks, bids } = orderBook;
    const { pip_size, defaultSlippage } = instrument;

    const form = useForm<LimitOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            limitOrderSchema({
                min_order_size: minOrderSize,
                order_decimals: orderDecimals,
                currencyOut: currency,
                currencyPair: pairMapToUse?.show
            })
        )
    });

    const totals = useTotals({
        form,
        pairMap: pairMapToUse,
        side,
        ccy1: instrument?.type === 'Future' ? instrument?.config?.ccy2 || '' : ccy1,
        ccy2: instrument?.type === 'Future' ? instrument?.config?.ccy2 || '' : ccy2,
        ccy2Order: isCcy2Order,
        bids,
        asks
    });

    const {
        watch,
        register,
        setValue,
        handleSubmit,
        formState: { isSubmitting, isValid }
    } = form;

    const [quantity] = watch(['quantity']);
    const [debouncedQuantity] = useDebouncedValue(quantity, 300);

    const { spotFeesApi, cfdFeesApi } = useTradeFeePreview({
        ...orderExecutionProps,
        side,
        isCfd: instrument.type === 'FX' ? false : true,
        debouncedQuantity
    });

    const { data: spotFees } = spotFeesApi;
    const { data: cfdFees } = cfdFeesApi;

    const fees = useMemo(() => spotFees || cfdFees, [spotFees, cfdFees]);

    const onSubmit = async (data: LimitOrderFormInput, e) => {
        const dataValidated = data as LimitOrderFormValues;
        if (dataValidated.quantity && credentials) {
            if (closeOnSubmit) onClose?.();
            lumeFxRestClient.placeOrder({
                Account: currentLuceraAccountName,
                Symbol: pairMapToUse?.celer,
                TimeInForce: dataValidated.duration.value as LumeFxTimeInForce,
                OrdType: LumeFxOrderType.Limit,
                Price: (dataValidated.limitPrice || 0).toString(),
                OrderQty: (data.quantity || 0).toString(),
                ClOrdID: crypto.randomUUID(),
                Side: side
            });
        }
    };

    const onError = (errors) => console.error(errors);

    const { limitPriceTooltip, isRestingLimitOrder, resetDefault } = useLimitOrder(form, props, '');

    const reloadState = useCallback(
        (...args) => {
            setStateLoading(true);
            resetDefault(...args);
            setTimeout(() => setStateLoading(false), 300);
        },
        [resetDefault, setStateLoading]
    );

    useDidUpdate(() => {
        if (credentials) {
            if (debouncedQuantity && debouncedQuantity > 0) {
                // console.log('addMarketFull due to quantity', market.celer);
                addMarketFull(pairMapToUse.celer);
            }
        }
    }, [debouncedQuantity]);

    useDidUpdate(() => reloadState(), [currency]);
    useDidUpdate(() => {
        if (prefillValues) {
            setSide((prefillValues.netQtyPosition || 0) < 0 ? LumeFxSide.SELL : LumeFxSide.BUY);
            const limitPrice = +Math.abs(prefillValues.price).toFixed(ccy1Config.order_decimals);
            const quantity = +Math.abs(prefillValues.netQtyPosition).toFixed(ccy1Config.order_decimals);
            reloadState(limitPrice, quantity);
        }
    }, [prefillValues]);

    useEffect(() => {
        if (prefillValues) {
            setSide((prefillValues.netQtyPosition || 0) < 0 ? LumeFxSide.SELL : LumeFxSide.BUY);
            const limitPrice = +Math.abs(prefillValues.price).toFixed(ccy1Config.order_decimals);
            const quantity = +Math.abs(prefillValues.netQtyPosition).toFixed(ccy1Config.order_decimals);
            reloadState(limitPrice, quantity);
        } else reloadState();
    }, [pairMapToUse.celer]);

    return (
        <FormProvider {...form}>
            <form
                onSubmit={handleSubmit(onSubmit, onError)}
                className="relative flex flex-col h-full w-full text-brand-text">
                <FormBody side={side} mode={mode === 'popup' ? 'Modal' : 'Module'}>
                    <FormOrderTypeSwitch initialValue={side} onChange={setSide} />
                    {stateLoading ? (
                        <div className="mb-2">
                            <Loader />
                        </div>
                    ) : (
                        <div>
                            <Tooltip content={limitPriceTooltip || ''} className="">
                                <RHFNumberInput
                                    label={`${lang.orderForm.limitPrice} *`}
                                    placeholder={lang.orderForm.limitPrice}
                                    {...register('limitPrice')}
                                    disabled={!isRestingLimitOrder}
                                    className={{
                                        'text-brand-red': limitPriceTooltip,
                                        'text-brand-text': !limitPriceTooltip
                                    }}
                                    step={BigNumber(1)
                                        .dividedBy(Math.pow(10, pip_size || 0))
                                        .toNumber()}
                                    currency={priceCurrency.show}
                                />
                            </Tooltip>
                            {(screen.mobile || screen.tablet || isTablet) && (
                                <span className="text-brand-red italic">{limitPriceTooltip}</span>
                            )}
                        </div>
                    )}
                    <ExecutionInputController name="duration" label={`${lang.orderForm.duration} *`}>
                        <Select type="execution" options={durationOptions} />
                    </ExecutionInputController>
                    <RHFNumberInput
                        {...register('quantity')}
                        label={`${lang.orderForm.quantity} *`}
                        placeholder={lang.orderForm.quantity}
                        min={minOrderSize}
                        step={activeCurrencyConfig.increments}
                        currency={currency}
                    />
                    <SlippageInput
                        {...register('slippage')}
                        label={lang.orderForm.slippage}
                        placeholder={lang.orderForm.slippage}
                        disabled={isRestingLimitOrder}
                        defaultSlippage={defaultSlippage}
                        step={0.1}
                    />
                    <Totals {...totals} />
                    {instrument.type === 'FX' && quantity && (
                        <FeePreview
                            feesDetails={spotFees}
                            isLoadingFeesDetails={spotFeesApi.isLoading || spotFeesApi.isValidating}
                            feesDetailsError={spotFeesApi.error}
                        />
                    )}
                    {instrument.type !== 'FX' && quantity && (
                        <FeePreview
                            feesDetails={cfdFees}
                            isLoadingFeesDetails={cfdFeesApi.isLoading || cfdFeesApi.isValidating}
                            feesDetailsError={cfdFeesApi.error}
                        />
                    )}
                    {totals.total !== '-' && debouncedQuantity && (
                        <BalancePreview
                            fees={fees}
                            totals={totals}
                            quantity={debouncedQuantity}
                            parentSize={parentSize}
                            {...orderExecutionProps}
                        />
                    )}
                </FormBody>
                <FormFooter mode={mode === 'popup' ? 'Modal' : 'Module'}>
                    <FormSubmit
                        side={side}
                        isValid={isValid}
                        disabled={isSubmitting || !isValid || totals.disableSubmit || !instrument.type}
                        noInstrumentConfig={!instrument.type}
                        noSettlementDate={!orderBook.settlementDate}
                        className={mode === 'popup' ? 'left-6' : ''}
                    />
                </FormFooter>
            </form>
        </FormProvider>
    );
}

export default LimitOrderForm;
